import { ActivatedRouteSnapshot } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

export class AppAnalytics {
  private static appInsights: ApplicationInsights;

  public static init(): boolean {
    if (this.appInsights) {
      return true;
    }

    if (environment.appInsightsConfig && environment.appInsightsConfig.instrumentationKey) {
      this.appInsights = new ApplicationInsights({
        config: environment.appInsightsConfig
      });

      this.appInsights.loadAppInsights();

      console.log('analytics initialized');

      return true;
    }

    return false;
  }

  public static setUserId(userId: string | undefined): void {
    const appInsights = AppAnalytics.getAppInsights();
    if (appInsights) {
      if (userId) {
        appInsights.setAuthenticatedUserContext(userId);
      }
      else {
        appInsights.clearAuthenticatedUserContext();
      }
    }
  }

  public static pageView(name: string, url?: string, properties?: { [key: string]: string }, duration?: string) {
    const appInsights = AppAnalytics.getAppInsights();
    if (appInsights) {
      appInsights.trackPageViewPerformance({
        name,
        uri: url,
        properties: this.getProperties(properties),
        duration
      });
    }
  }

  public static event(name: string, properties?: { [key: string]: string }) {
    const appInsights = AppAnalytics.getAppInsights();
    if (appInsights) {
      appInsights.trackEvent({
        name,
        properties: this.getProperties(properties)
      });
    }
  }

  /**
   * Only for errors that actually broke the application. Logged errors go to {@link trace}.
   */
  public static error(error: Error, properties?: { [key: string]: string }) {
    const appInsights = AppAnalytics.getAppInsights();
    if (appInsights) {
      appInsights.trackException({
        exception: error,
        severityLevel: 3,
        properties: this.getProperties(properties)
      });
    }
  }

  public static trace(message: string, severityLevel: number, properties?: { [key: string]: string }) {
    const appInsights = AppAnalytics.getAppInsights();
    if (appInsights) {
      appInsights.trackTrace({
        message,
        severityLevel,
        properties: this.getProperties(properties)
      });
    }
  }

  /**
   * Flattens a value into string properties without ever throwing. JSON.stringify fails on the
   * circular references Angular attaches to its errors.
   */
  public static toProperties(value: any): { [key: string]: string } {
    if (value === null || value === undefined || typeof value !== 'object') {
      return { value: String(value) };
    }

    const properties: { [key: string]: string } = {};

    if (value instanceof Error) {
      try {
        properties['name'] = value.name;
        properties['message'] = value.message;
        properties['stack'] = value.stack ?? '';
      } catch {
        //a throwing getter must not cost us the remaining properties
      }
    }

    let keys: string[] = [];

    try {
      keys = Object.keys(value);
    } catch {
      return properties;
    }

    for (const key of keys) {
      try {
        properties[key] = AppAnalytics.toText(value[key]);
      } catch {
        properties[key] = '[unreadable]';
      }
    }

    return properties;
  }

  private static toText(value: any): string {
    if (value === null || value === undefined || typeof value !== 'object') {
      return String(value);
    }

    try {
      return JSON.stringify(value);
    } catch {
      return Object.prototype.toString.call(value);
    }
  }

  private static getProperties(properties?: { [key: string]: string }): { [key: string]: string } {
    if (!properties) {
      properties = {};
    }

    properties['version'] = environment.version;
    properties['uwp'] = '' + environment.uwp;

    return properties;
  }

  public static navigation(root: ActivatedRouteSnapshot, urlAfterRedirects: string): void {
    const activatedComponent = this.getActivatedComponent(root);
    if (activatedComponent) {
      this.pageView(`${activatedComponent.name} ${this.getRouteTemplate(root)}`, urlAfterRedirects);
    }
  }

  private static getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private static getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';

    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }

  private static getAppInsights(): ApplicationInsights {
    if (this.appInsights == null) {
      AppAnalytics.init();
    }

    return this.appInsights;
  }
}
